/**
 * The Qdrant tab — the fifth pipeline: the QdrantMapper gateway and the sgp1
 * vector cluster behind it (repo: silverpush/qdrant-deploy).
 *
 * Everything renders from the mapper's own MongoDB metrics store; nothing on
 * this tab (or the server behind it) ever calls the mapper API or the cluster,
 * so the board structurally cannot add a microsecond to search latency.
 *
 * This is the board's one LIVE tab: hooks poll every 60 s (see queries.ts for
 * why that exception is safe here and unsafe elsewhere). Every tile therefore
 * states its own age — a number that stops moving must say so, not sit there
 * looking current. A dead stats sampler is CRIT, not silence: the 11-day
 * lesson applies to a metrics pipeline exactly as it does to an ES sink.
 *
 * Null is not zero throughout: "—" means not read yet, "unavailable" means
 * asked and failed, and a rendered 0 always means a measured zero.
 */
import { useEffect, useMemo, useState } from 'react'
import { PanelState, Pill, Stat, Tile, Bar } from '../components/ui'
import type { State } from '../components/ui'
import { Sparkline } from '../components/charts'
import './Qdrant.css'
import { n as fmtN, age as fmtAge, parseUtc } from '../components/format'
import {
  useQdrantClients, useQdrantCluster, useQdrantLatency, useQdrantOverview,
  useQdrantStatus, useQdrantThroughput, useQdrantTopQueries,
} from '../api/queries'
import type { QdrantTopParams } from '../api/queries'

/** Windows the server accepts; the tab's own chips (the board-level range
 *  chips speak days, this store holds 7 at most). */
const WINDOWS = ['15m', '1h', '6h', '24h', '7d'] as const
type Window = (typeof WINDOWS)[number]

const EXPECTED_VID_POINTS = 8_225_819 // ES source count, frozen 2026-08-22

function age(iso: string | undefined, cacheHit?: boolean): string {
  if (!iso) return ''
  const s = Math.max(0, Math.round((Date.now() - parseUtc(iso)) / 1000))
  const a = `${fmtAge(s)} ago`   // h/d aware — a 7d last_seen must not say 10080m
  return cacheHit ? `${a} · server cache` : a
}

/** The cannot-state register (DESIGN.md's fourth register): not zero, not an
 *  error — the server could not state it, and it must not wear ok/warn/crit. */
const Unstated = () => (
  <span className="unstated" title="the server cannot state this yet">not stated</span>
)

function WindowChips({ value, onChange }: {
  value: Window; onChange: (w: Window) => void
}) {
  return (
    <span className="chips" role="group" aria-label="window">
      {WINDOWS.map((w) => (
        <button key={w} className={`chip${w === value ? ' on' : ''}`}
          aria-pressed={w === value}
          onClick={() => onChange(w)}>{w}</button>
      ))}
    </span>
  )
}

/* ------------------------------------------------------------------ live */

/** Gateway-now strip: what is running, queued and admitted at this minute,
 *  and whether the breaker is letting traffic through at all. */
export function QdrantLive() {
  const q = useQdrantOverview()
  const gw = q.data?.gateway
  const breaker = gw?.breaker ?? null
  // a null breaker is CANNOT-STATE, never 'ok' — lead-off is not a flatline
  const breakerState: State | undefined =
    breaker === 'open' ? 'crit' : breaker === 'half_open' ? 'warn'
      : breaker ? 'ok' : undefined
  const samplerAge = q.data?.sampler?.age_s
  const sum = (a: number | null | undefined, b: number | null | undefined) =>
    a == null || b == null ? null : a + b
  const running = sum(gw?.search_in_flight, gw?.write_in_flight)
  const queued = sum(gw?.search_queue_depth, gw?.write_queue_depth)
  const slots = sum(gw?.search_capacity, gw?.write_capacity)
  return (
    <Tile title="gateway · now" hint="from the stats sampler's last minute"
      action={<span className="note">{age(q.data?.computed_at, q.data?.cache_hit)}</span>}>
      <PanelState isLoading={q.isLoading} error={q.error}>
        {q.data && (
          <>
            <div className="statrow">
              <Stat label="search qps · 60s"
                value={q.data.lanes.search?.qps_60s ?? null} unit="/s" />
              <Stat label="write qps · 60s"
                value={q.data.lanes.write?.qps_60s ?? null} unit="/s" />
              {running == null
                ? <Stat label="running now" value={null} sub={<Unstated />} />
                : <Stat label="running now" value={running}
                    sub={slots == null ? <Unstated /> : `of ${slots} per-worker slots`} />}
              {queued == null
                ? <Stat label="queued now" value={null} sub={<Unstated />} />
                : <Stat label="queued now" value={queued}
                    state={queued > 0 ? 'warn' : 'ok'} />}
              <Stat label="breaker" value={null}
                sub={breaker ?? <Unstated />}
                {...(breakerState ? { state: breakerState } : {})} />
            </div>
            {/* the sampler is this tab's own upstream; its silence is CRIT */}
            <p className={samplerAge !== undefined && samplerAge > 300 ? 'note qd-crit' : 'note'}>
              {samplerAge === undefined
                ? <>sampler: <Unstated /></>
                : `sampler heartbeat ${Math.round(samplerAge)}s ago · cycle ${q.data.sampler?.cycle_ms ?? '—'}ms · ${q.data.sampler?.errors ?? '—'} errors`}
            </p>
          </>
        )}
      </PanelState>
    </Tile>
  )
}

/* ------------------------------------------------------------ throughput */

export function QdrantThroughput() {
  const [win, setWin] = useState<Window>('1h')
  const q = useQdrantThroughput(win)
  const lanes = useMemo(() => {
    const by: Record<string, { qps: number[]; errors: number; sheds: number; n: number }> = {}
    for (const s of q.data?.series ?? []) {
      const b = (by[s.lane] ??= { qps: [], errors: 0, sheds: 0, n: 0 })
      b.qps.push(s.qps)
      b.errors += s.errors
      b.sheds += s.sheds
      b.n += s.n
    }
    return by
  }, [q.data])
  return (
    <Tile title="throughput" span
      hint="requests per second by lane, bucketed"
      action={<WindowChips value={win} onChange={setWin} />}>
      <PanelState isLoading={q.isLoading} error={q.error}>
        {q.data && (
          <div className="statrow">
            {(['search', 'write'] as const).map((lane) => {
              const b = lanes[lane]
              return (
                <div key={lane} className="lane-block">
                  <Stat label={`${lane} · total`}
                    value={b?.n ?? 0} unit=" req"
                    sub={`${b?.errors ?? 0} errors · ${b?.sheds ?? 0} queued/shed`}
                    state={b && b.errors > 0 ? 'warn' : 'ok'} />
                  {b && b.qps.length > 1
                    ? <Sparkline values={b.qps} label={`${lane} qps`} />
                    : <p className="note">not enough buckets to draw</p>}
                </div>
              )
            })}
          </div>
        )}
        <p className="note">{age(q.data?.computed_at, q.data?.cache_hit)}</p>
      </PanelState>
    </Tile>
  )
}

/* --------------------------------------------------------------- latency */

const PHASES = ['total', 'cluster', 'overhead', 'queue'] as const

export function QdrantLatency() {
  const [win, setWin] = useState<Window>('1h')
  const q = useQdrantLatency(win)
  return (
    <Tile title="latency" span
      hint="successful requests only; overhead is the gateway's own compute, queue is deliberate backpressure"
      action={<WindowChips value={win} onChange={setWin} />}>
      <PanelState isLoading={q.isLoading} error={q.error}>
        {q.data && (
          <div style={{ overflowX: 'auto' }}>
          <table className="t tnum">
            <thead>
              <tr>
                <th rowSpan={2}>lane</th><th className="n" rowSpan={2}>n</th>
                {PHASES.map((p) => (
                  <th key={p} className="n" colSpan={3} scope="colgroup">{p} · ms</th>
                ))}
              </tr>
              <tr>
                {PHASES.map((p) => (
                  ['p50', 'p95', 'p99'].map((q) => (
                    <th key={`${p}${q}`} className="n" scope="col">{q}</th>
                  ))
                ))}
              </tr>
            </thead>
            <tbody>
              {q.data.lanes.map((l) => (
                <tr key={l.lane}>
                  <td>{l.lane}</td>
                  <td className="n">{fmtN(l.n)}</td>
                  {PHASES.map((p) => {
                    const v = l[p]
                    return [
                      <td key={`${p}50`} className="n">{v.p50 ?? '—'}</td>,
                      <td key={`${p}95`} className="n">{v.p95 ?? '—'}</td>,
                      <td key={`${p}99`} className="n">{v.p99 ?? '—'}</td>,
                    ]
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        <p className="note">{age(q.data?.computed_at, q.data?.cache_hit)}</p>
      </PanelState>
    </Tile>
  )
}

/* ------------------------------------------------------------ status mix */

const CLASS_LABEL: Record<string, string> = {
  done: 'done',
  client_error: 'client error (4xx)',
  queued_or_shed: 'queued/shed (429)',
  server_error: 'server error (5xx)',
}
// client_error (4xx, the caller's own fault) is 'advisory' -- the SAME
// register QdrantClients uses for the identical event, so a 4xx reads the
// same severity in both places rather than two different ones three
// sections apart. queued_or_shed stays 'warn': a shed is backpressure
// working as designed, not the caller's fault and not a failure either.
const CLASS_STATE: Record<string, State> = {
  done: 'ok', client_error: 'advisory', queued_or_shed: 'warn', server_error: 'crit',
}

export function QdrantStatusMix() {
  const [win, setWin] = useState<Window>('1h')
  const q = useQdrantStatus(win)
  const agg = useMemo(() => {
    const by: Record<string, number> = {}
    let total = 0
    for (const r of q.data?.rows ?? []) {
      by[r.class] = (by[r.class] ?? 0) + r.n
      total += r.n
    }
    return { by, total }
  }, [q.data])
  return (
    <Tile title="request outcomes"
      hint="a shed 429 is backpressure doing its job — it is separated from errors, never hidden in them"
      action={<WindowChips value={win} onChange={setWin} />}>
      <PanelState isLoading={q.isLoading} error={q.error}>
        {q.data && (agg.total === 0
          ? <p className="note">no requests in this window</p>
          : (
            <div className="outcomes">
              {Object.entries(CLASS_LABEL).map(([cls, label]) => {
                const n = agg.by[cls] ?? 0
                return (
                  <div key={cls} className="outcome-row">
                    <Pill state={n === 0 ? 'ok' : CLASS_STATE[cls] ?? 'ok'}>{label}</Pill>
                    <span className="tnum">{fmtN(n)}</span>
                    <Bar pct={agg.total ? (100 * n) / agg.total : 0}
                      state={n === 0 ? 'ok' : CLASS_STATE[cls] ?? 'ok'} />
                  </div>
                )
              })}
            </div>
          ))}
        <p className="note">{age(q.data?.computed_at, q.data?.cache_hit)}</p>
      </PanelState>
    </Tile>
  )
}

/* -------------------------------------------------------------- clients */

/** Who is loading the cluster. One row per mapper client key, `overall`
 *  pinned first and visually distinguished — the row a reader checks before
 *  asking which caller's query is slow. Clicking a caller's row scopes the
 *  top-queries panel below to it; `overall` is a summary, not a caller, and
 *  is deliberately not clickable.
 *
 *  `selected` names whichever caller currently scopes the top-queries panel
 *  (if any), purely so this table can mark it — App owns the actual filter
 *  state; this component never reads it back. */
export function QdrantClients({ onPick, selected }: {
  onPick?: (c: string) => void; selected?: string
}) {
  const [win, setWin] = useState<Window>('1h')
  const q = useQdrantClients(win)
  // Tagged by POSITION, not by comparing `client` to the literal string
  // 'overall' — a real caller key that happened to be named that would
  // otherwise be mistaken for the summary row and rendered inert. `overall`
  // on the response is always exactly one row; `clients` is the rest.
  const rows = q.data
    ? [
        { ...q.data.overall, isOverall: true as const },
        ...q.data.clients.map((c) => ({ ...c, isOverall: false as const })),
      ]
    : []
  const hasClients = (q.data?.clients.length ?? 0) > 0
  return (
    <Tile title="clients" span
      hint="per mapper client key — overall first, then each caller"
      action={<WindowChips value={win} onChange={setWin} />}>
      <PanelState isLoading={q.isLoading} error={q.error}>
        {q.data && (
          <div style={{ overflowX: 'auto' }}>
            <table className="t tnum">
              <thead>
                <tr>
                  <th scope="col">client</th>
                  <th className="n" scope="col">req</th>
                  <th className="n" scope="col">qps</th>
                  <th className="n" scope="col">p50 ms</th>
                  <th className="n" scope="col">p95 ms</th>
                  <th className="n" scope="col">5xx</th>
                  <th className="n" scope="col">4xx</th>
                  <th className="n" scope="col">shed</th>
                  <th className="n" scope="col">last seen</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const clickable = !r.isOverall && !!onPick
                  const isSelected = !r.isOverall && r.client === selected
                  const cls = [
                    r.isOverall ? 'qd-overall' : clickable ? 'qd-pick' : '',
                    isSelected ? 'qd-selected' : '',
                  ].filter(Boolean).join(' ') || undefined
                  return (
                    <tr key={i} className={cls}
                      onClick={clickable ? () => onPick!(r.client) : undefined}>
                      <td>
                        {/* A real <button>, the same .th-btn pattern the
                            sortable column headers already use below — it
                            inherits font/colour and comes with correct
                            semantics and native keyboard activation for
                            free. The ROW keeps its own onClick too (below):
                            `.qd-pick`'s cursor and hover span all nine
                            columns, so a row-only click target would leave
                            req/qps/p95/last-seen looking clickable while
                            doing nothing — a lying affordance, the opposite
                            of what null-is-not-zero discipline is for
                            elsewhere on this tab. stopPropagation here is
                            what stops the button's own click from ALSO
                            bubbling into the row's handler and firing
                            onPick twice. */}
                        {clickable
                          ? <button className="th-btn"
                              onClick={(e) => { e.stopPropagation(); onPick!(r.client) }}>
                              {r.client}
                            </button>
                          : r.client}
                      </td>
                      <td className="n">{fmtN(r.requests)}</td>
                      <td className="n">{r.qps}</td>
                      <td className="n">{r.p50_ms == null ? <Unstated /> : r.p50_ms}</td>
                      <td className="n">{r.p95_ms == null ? <Unstated /> : r.p95_ms}</td>
                      {/* three different events, three different registers: an
                          error is the cluster failing the caller, a client
                          error is the caller's own malformed request, and a
                          shed is backpressure working as designed — never an
                          error, so it never wears crit. (Beating ui.css's
                          `table.t td.n` on specificity is Qdrant.css's job —
                          see the `table.t td.n.qd-*` rules there.) */}
                      <td className={`n${r.errors > 0 ? ' qd-crit' : ''}`}>{r.errors}</td>
                      <td className={`n${r.client_errors > 0 ? ' qd-advisory' : ''}`}>{r.client_errors}</td>
                      <td className={`n${r.sheds > 0 ? ' qd-warn' : ''}`}>{r.sheds}</td>
                      <td className="n">
                        {r.last_seen == null ? <Unstated /> : age(r.last_seen)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="note">
          {age(q.data?.computed_at, q.data?.cache_hit)}
          {/* `overall` always exists and is real, measured data — a window
              with no per-caller breakdown still gets its own explanation
              rather than a table that trails off with no per-client rows
              and no explanation. And the "click a client" hint must not
              claim an affordance this render does not have: it only shows
              when there both IS a client to click and a handler that would
              do something with the click. */}
          {q.data && !hasClients ? ' · no individual clients reported in this window'
            : q.data && onPick ? ' · click a client to filter top queries' : ''}
        </p>
      </PanelState>
    </Tile>
  )
}

/* ------------------------------------------------------------ top queries */

const SORTABLE: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'qps', label: 'qps' },
  { key: 'count', label: 'count' },
  { key: 'p50', label: 'p50 ms' },
  { key: 'p95', label: 'p95 ms' },
  { key: 'errors', label: '5xx' },
  { key: 'sheds', label: 'shed' },
  { key: 'last_seen', label: 'last seen' },
]

/** Top query signatures. A "query" here is a signature — (lane, endpoint,
 *  collection, client, k, fields) — because the store keeps one document per
 *  completed request, not per unique vector. Individual in-flight queries are
 *  deliberately not persisted (writing before completion would put Mongo on
 *  the request path); "running/queued now" lives in the gateway strip above,
 *  from the sampler. This table is the completed/failed/shed record. */
export function QdrantTopQueries(
  { client, onClientChange }:
    { client?: string; onClientChange?: (c: string) => void } = {},
) {
  const [p, setP] = useState<QdrantTopParams>({
    window: '1h', lane: '', status: 'all', sort: 'qps', dir: 'desc',
    collection: '', endpoint: '', client: client ?? '',
  })
  // A click in the clients table scopes this panel to that caller. Only
  // reacts to the PROP changing, not to every render, so clearing the filter
  // via this panel's own client select still works undisturbed.
  useEffect(() => {
    if (client !== undefined) setP((prev) => ({ ...prev, client }))
  }, [client])
  // The back-channel: whatever `p.client` actually IS right now — seeded
  // from a click above, or changed locally via this panel's own select —
  // is reported upward. This is what lets a caller (App) keep an accurate
  // "currently scoping" marker (QdrantClients' `selected` prop) instead of
  // a stale echo of the last command it happened to send down; it is also
  // what makes a re-pick of the SAME client, right after clearing the
  // filter down here, a real change rather than a silent no-op — the
  // caller's own copy of `client` was already corrected to '' by this same
  // channel the moment the filter was cleared, so picking `classifier`
  // again genuinely differs from the caller's last-known value even though
  // the string itself repeats. (An earlier version of this fix used a
  // separate nonce prop instead; keeping the caller's copy of the value
  // itself truthful removes the need for one.)
  useEffect(() => {
    onClientChange?.(p.client)
    // `onClientChange` IS a dependency, not an oversight: App passes the
    // stable `setQdrantClient` setter today, so this fires only on a real
    // `p.client` change either way -- but a future caller passing an inline
    // arrow must still get a fresh closure each time, or the back-channel
    // silently stops reporting and both the dead-click and stale-marker
    // bugs this effect exists to prevent come back with no test failing.
  }, [p.client, onClientChange])
  const q = useQdrantTopQueries(p)
  const set = (patch: Partial<QdrantTopParams>) => setP((old) => ({ ...old, ...patch }))
  const sortBy = (key: string) => set(p.sort === key
    ? { dir: p.dir === 'desc' ? 'asc' : 'desc' }
    : { sort: key, dir: 'desc' })
  // filter options come from the data itself, so the dropdowns never offer a
  // value the window cannot contain
  const opts = useMemo(() => {
    const u = (f: (r: NonNullable<typeof q.data>['rows'][number]) => string | null | undefined) =>
      [...new Set((q.data?.rows ?? []).map(f).filter((x): x is string => !!x))].sort()
    return { collections: u((r) => r.collection), endpoints: u((r) => r.endpoint), clients: u((r) => r.client) }
  }, [q.data])
  return (
    <Tile title="top queries" span
      hint="signatures, heaviest first — click a column to re-sort server-side"
      action={<WindowChips value={p.window as Window} onChange={(w) => set({ window: w })} />}>
      <div className="filters">
        <select aria-label="lane" value={p.lane} onChange={(e) => set({ lane: e.target.value })}>
          <option value="">both lanes</option>
          <option value="search">search</option>
          <option value="write">write</option>
        </select>
        <select aria-label="outcome" value={p.status} onChange={(e) => set({ status: e.target.value })}>
          <option value="all">all outcomes</option>
          <option value="done">done</option>
          <option value="client_error">client error</option>
          <option value="queued_or_shed">queued/shed</option>
          <option value="server_error">server error</option>
        </select>
        <select aria-label="collection" value={p.collection} onChange={(e) => set({ collection: e.target.value })}>
          <option value="">all collections</option>
          {opts.collections.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select aria-label="endpoint" value={p.endpoint} onChange={(e) => set({ endpoint: e.target.value })}>
          <option value="">all endpoints</option>
          {opts.endpoints.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select aria-label="client" value={p.client} onChange={(e) => set({ client: e.target.value })}>
          <option value="">all clients</option>
          {opts.clients.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <PanelState isLoading={q.isLoading} error={q.error}>
        {q.data && (q.data.rows.length === 0
          ? <p className="note">nothing matched this window and filter</p>
          : (
            <div style={{ overflowX: 'auto' }}>
            <table className="t tnum">
              <thead>
                <tr>
                  <th>lane</th><th>endpoint</th><th>collection</th><th>client</th>
                  <th className="n">k</th><th className="n">fields</th>
                  {SORTABLE.map((c) => (
                    <th key={c.key} className="n th-sort"
                      aria-sort={p.sort === c.key
                        ? (p.dir === 'desc' ? 'descending' : 'ascending') : 'none'}>
                      <button className="th-btn" onClick={() => sortBy(c.key)}>
                        {c.label}{p.sort === c.key ? (p.dir === 'desc' ? ' ↓' : ' ↑') : ''}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {q.data.rows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.lane ?? '—'}</td>
                    <td>{r.endpoint ?? '—'}</td>
                    <td>{r.collection ?? '—'}</td>
                    <td>{r.client ?? '—'}</td>
                    <td className="n">{r.k ?? '—'}</td>
                    <td className="n">{r.n_fields ?? '—'}</td>
                    <td className="n">{r.qps}</td>
                    <td className="n">{fmtN(r.count)}</td>
                    <td className="n">{r.p50 ?? '—'}</td>
                    <td className="n">{r.p95 ?? '—'}</td>
                    <td className={`n${r.errors ? ' qd-crit' : ''}`}>{r.errors}</td>
                    <td className={`n${r.sheds ? ' qd-warn' : ''}`}>{r.sheds}</td>
                    <td className="n">{age(r.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          ))}
        <p className="note">{age(q.data?.computed_at, q.data?.cache_hit)}</p>
      </PanelState>
    </Tile>
  )
}

/* --------------------------------------------------------------- cluster */

export function QdrantClusterPanel() {
  const [win, setWin] = useState<Window>('6h')
  const over = useQdrantOverview()
  const q = useQdrantCluster(win)
  const byNode = useMemo(() => {
    const m: Record<string, number[]> = {}
    for (const s of q.data?.node_series ?? []) {
      if (s.cpu !== null) (m[s.node] ??= []).push(s.cpu)
    }
    return m
  }, [q.data])
  return (
    <Tile title="cluster" span
      hint="the three sgp1 data nodes and both collections, from the sampler"
      action={<WindowChips value={win} onChange={setWin} />}>
      <PanelState isLoading={over.isLoading || q.isLoading} error={over.error ?? q.error}>
        <div className="statrow">
          {(over.data?.nodes ?? []).map((n) => {
            const memPct = n.mem_total_mb && n.mem_avail_mb !== null && n.mem_avail_mb !== undefined
              ? Math.round(100 * (1 - n.mem_avail_mb / n.mem_total_mb)) : null
            return (
              <div key={n.meta.node} className="lane-block">
                <Stat label={n.meta.node}
                  value={n.cpu_busy_pct ?? null} unit="% cpu"
                  {...(n.cpu_busy_pct == null ? {}   // unstated drives no state
                    : { state: (n.cpu_busy_pct > 85 ? 'crit'
                        : n.cpu_busy_pct > 65 ? 'warn' : 'ok') as State })}
                  sub={<>mem {memPct ?? <Unstated />}% used · disk {n.disk_avail_gb ?? <Unstated />} GB free · load {n.load1 ?? <Unstated />}</>} />
                {byNode[n.meta.node] && byNode[n.meta.node]!.length > 1
                  && <Sparkline values={byNode[n.meta.node]!} label="cpu %" />}
              </div>
            )
          })}
        </div>
        <div className="coll-rows">
          {(over.data?.collections ?? []).map((c) => {
            // only a STATED points figure may claim migration progress — a
            // null must not render as "migrating · 0.0%"
            const migrating = c.name === 'vid_classification'
              && c.points != null && c.points < EXPECTED_VID_POINTS
            const st: State = (c.shards_dead ?? 0) > 0 || c.status === 'red' ? 'crit'
              : c.status !== 'green' ? 'warn' : 'ok'
            return (
              <div key={c.name} className="outcome-row">
                <Pill state={st}>{c.name}</Pill>
                <span className="tnum">
                  {c.points != null ? fmtN(c.points) : <Unstated />} pts
                </span>
                <span className="note">
                  {c.status ?? '—'} · {c.shards_active ?? '—'} shards active
                  {c.shards_dead ? ` · ${c.shards_dead} DEAD` : ''}
                  {c.segments ? ` · ${c.segments} segments` : ''}
                </span>
                {migrating && c.points != null && (
                  <span className="note tnum">
                    migrating · {(100 * c.points / EXPECTED_VID_POINTS).toFixed(1)}%
                  </span>
                )}
              </div>
            )
          })}
          {over.data?.raft && (
            <p className={over.data.raft.leader_present === false ? 'note qd-crit' : 'note'}>
              raft: {over.data.raft.leader_present === false ? 'NO LEADER' : 'leader present'}
              {' '}· {over.data.raft.peers ?? '—'} peers
              · {over.data.raft.pending_operations ?? <Unstated />} pending ops
            </p>
          )}
        </div>
        <p className="note">{age(q.data?.computed_at, q.data?.cache_hit)}</p>
      </PanelState>
    </Tile>
  )
}

/** Rail-headline helper: search QPS + worst-wins health, straight from the
 *  server's own verdict so rail and tab can never disagree.
 *
 *  Deliberately `'ok' | 'warn' | 'crit' | 'idle'`, NOT the shared `State`
 *  type (which now also carries `'advisory'` for the clients/status-mix
 *  registers): `QdrantOverview.state` is itself `z.enum(['ok','warn',
 *  'crit'])`, so this can never actually produce `'advisory'`, and
 *  `RailEntry['state']` (PipelineTabs.tsx) has no such tier to receive one. */
export function useQdrantRail(): {
  value: number | null | undefined
  state: 'ok' | 'warn' | 'crit' | 'idle'
  note?: string
} {
  const q = useQdrantOverview()
  if (q.isError) return { value: null, state: 'crit', note: 'qdrant metrics unreachable' }
  if (!q.data) return { value: undefined, state: 'idle' }
  return {
    // absent lane = cannot state, and the rail says "unavailable" — a
    // fabricated 0 qps is the wedged-gateway signature (null is not zero)
    value: q.data.lanes.search?.qps_60s ?? null,
    state: q.data.state,
    ...(q.data.note ? { note: q.data.note } : {}),
  }
}
